<template>
    <div>
        <div v-for="(_, key) in reconstructions" class="mb-3">
            <ReconstructionControl :value="reconstructions[key]" @input="reconstructions[key] = $event"
                                   :validate="validate"
                                   :key="key"
                                   :type-options="typeOptions"
                                   :extent-options="extentOptions"
                                   @remove="removeReconstruction(key)"/>
        </div>
        <button class="btn btn-outline-primary" @click="addReconstruction()">
          {{ $t('form.reconstructions.buttonAddReconstruction')}}
        </button>
    </div>
</template>

<script lang="ts">
import {Control} from '@/composables/Control';
import {highestKey} from '@/utils';
import {fromPairs} from 'lodash-es';
import {defineComponent, onMounted, PropType, ref, watch} from 'vue';
import ReconstructionControl from './ReconstructionControl.vue';
import {Reconstruction} from './values';

interface Values { [key: number]: (Reconstruction | null); }

export default defineComponent({
    components: {ReconstructionControl},
    props: {
        value: {
            type: Array as PropType<Reconstruction[] | null>,
            default: null,
        },
        validate: {
            type: Boolean,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        typeOptions: {
            type: Array as PropType<string[]>,
            required: true,
        },
        extentOptions: {
            type: Array as PropType<string[]>,
            required: true,
        },
    },
    emits: ['input', 'validated'],
    setup(props, {emit}) {
        const reconstructions = ref<Values>({});

        const {updateValue, isInvalid} = Control<Reconstruction[]>(props, emit, {
            getValue: () => Object.values(reconstructions.value).filter(r => r !== null),
            validateCurrentValue: () => Object.values(reconstructions.value).every(r => r !== null),
        });

        onMounted(() => {
            const value = props.value;

            if (value !== null) {
                reconstructions.value = fromPairs([...value.entries()]);
            }

            updateValue();
        });

        watch(reconstructions, () => {
            updateValue();
        }, {deep: true});

        function addReconstruction(): void {
            const recs = Object.assign({}, reconstructions.value);

            recs[highestKey(recs) + 1] = null;

            reconstructions.value = recs;
        }

        function removeReconstruction(key: number): void {
            const recs = Object.assign({}, reconstructions.value);

            delete recs[key];

            reconstructions.value = recs;
        }

        return {
            reconstructions,
            addReconstruction,
            removeReconstruction,
            isInvalid,
        };
    },
});
</script>
